import React, { useState } from "react";
import { Container, Row, Table } from "reactstrap";
import useGetApplicators, { ApplicatorDto } from "../../requests/useGetApplicators";
import useDisplayMessage from "../Common/useDisplayMessage";
import FilterInput, { textSatisfiesFilter } from "../FilterInput";
import useRemoveApplicator from "../../requests/useRemoveApplicator";
import { CenteredSpinner } from "../Common/CenteredSpinner";
import PermissionButton from "../UserAccess/PermissionButton";
import KaAlert from "../Common/KaAlert";
import { Button } from "react-bootstrap";
import { CreateEditApplicator } from "./CreateEditApplicators";
import { modalMode } from "../Common/EntityModal";
import Confirm from "../Common/Confirm";
import EditViewButton from "../UserAccess/EditViewButton";
import { usePermission } from "../UserAccess/usePermission";
import { SiteFilterPill, useSiteFilter } from "../UserAccess/SiteScope/SiteFilterPill";
import { SiteColumnCell, SiteColumnHeader } from "../Common/SiteColumn";
import { ActionsColumnHeader } from "../Common/ActionsColumn";

enum States {
  None,
  AddEditApplicator,
}

type PopupState =
  {
    state: States.None;
    applicator?: null;
  } |
  {
    state: States.AddEditApplicator;
    applicator: ApplicatorDto | null;
  };

const alphabeticallyByName = (a: ApplicatorDto, b: ApplicatorDto) => a.name.localeCompare(b.name);

const ApplicatorPage = () => {
  const displayMessage = useDisplayMessage();
  const [filterText, setFilterText] = useState('');
  const [popupState, setPopupState] = useState<PopupState>({ state: States.None });
  const [applicatorToRemove, setApplicatorToRemove] = useState<ApplicatorDto | undefined>(undefined);
  const canModify = usePermission('otherEntitiesModify');
  const siteFilter = useSiteFilter();

  const getApplicator = useGetApplicators();

  const containsFilterText = (applicator: ApplicatorDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(applicator.name)
      || satisfiesFilter(applicator.license)
      || satisfiesFilter(applicator.epaNumber);
  };

  const filterTextChanged = (text: string) => setFilterText(text);

  const removeSuccess = () => {
    displayMessage.success(`Applicator ${applicatorToRemove?.name} removed`);
    return getApplicator.query();
  };

  const removeRequest = useRemoveApplicator(applicatorToRemove?.id ?? '', {
    onSuccess: removeSuccess,
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setApplicatorToRemove(undefined),
  });

  const createClicked = () => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditApplicator, applicator: null });
  }

  const editClicked = (applicator: ApplicatorDto) => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditApplicator, applicator: applicator });
  }

  const applicatorUpserted = (applicatorId: string, applicatorName: string) => {
    displayMessage.success(`Applicator ${applicatorName} ${popupState.applicator ? 'updated' : 'created'}`);
    getApplicator.query();
  }

  const editDismissed = () => setPopupState({ state: States.None });

  const visibleApplicators = (getApplicator.data ?? [])
    .filter(containsFilterText)
    .filter(applicator => siteFilter.matches(applicator.sites))
    .sort(alphabeticallyByName);

  const loadingRows = getApplicator.isLoading || removeRequest.isLoading;

  if (getApplicator.isError) return <h3>{getApplicator.error?.message}</h3>

  return <Container>
    {popupState.state === States.AddEditApplicator &&
      <CreateEditApplicator
        initialApplicator={popupState.applicator}
        onUpsert={applicatorUpserted}
        mode={modalMode(canModify, !popupState.applicator)}
        onDismiss={editDismissed} />}
    <Confirm
      visible={!!applicatorToRemove}
      title='Remove applicator'
      body={`Are you sure that you want to remove applicator ${applicatorToRemove?.name}?`}
      isLoading={removeRequest.isLoading}
      onDismiss={() => setApplicatorToRemove(undefined)}
      onConfirm={() => removeRequest.request()} />
    <Row className='justify-content-between mb-2'>
      <h2 className='col-auto ka-blue'>Applicators</h2>
      <PermissionButton permission="otherEntitiesModify" className='col-auto btn-ghost-primary' onClick={createClicked}>Create Applicator</PermissionButton>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <form>
      <div className='row gx-2 gx-md-3 mb-3'>
        <div className='col-md-4 mb-2 mb-md-0'>
          <FilterInput placeholder='Search by name, license or EPA number' onFilterTextChange={filterTextChanged} value={filterText} />
        </div>
        <div className='col-auto d-flex align-items-center'>
          <SiteFilterPill filter={siteFilter} />
        </div>
      </div>
    </form>
    <Table bordered className="preserve-whitespace" style={{tableLayout: "fixed"}}>
      <thead>
        <tr>
          <th className="col-3">Name</th>
          <th className="col-1">License</th>
          <th className="col-2">EPA Number</th>
          <th>Email(s)</th>
          <SiteColumnHeader />
          <ActionsColumnHeader />
        </tr>
      </thead>
      <tbody>
        {!loadingRows && visibleApplicators.map(applicator =>
          <tr key={applicator.id}>
            <td>{applicator.name}</td>
            <td>{applicator.license}</td>
            <td>{applicator.epaNumber}</td>
            <td className="text-break">{applicator.emailAddresses?.map(e => <div>{e}</div>)}</td>
            <SiteColumnCell sites={applicator.sites} />
            <td align="right" className="text-nowrap">
              <div className='dropdown'>
                <Button id='actionsMenuButton'
                  className='dropdown-toggle btn-ghost-secondary btn-link'
                  variant='link'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'>
                  <strong>Actions</strong>
                </Button>
                <div className='dropdown-menu' aria-labelledby='actionsMenuButton'>
                  <EditViewButton
                    canModify={canModify}
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => editClicked(applicator)} />
                  <PermissionButton permission="otherEntitiesModify"
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setApplicatorToRemove(applicator)}>Remove</PermissionButton>
                </div>
              </div>
            </td>
          </tr>)}
        {(loadingRows || visibleApplicators.length === 0) &&
          <tr>
            <td colSpan={6} className='text-center text-muted py-3'>
              {loadingRows
                ? <CenteredSpinner />
                : siteFilter.emptyMessage('No applicators to show')}
            </td>
          </tr>}
      </tbody>
    </Table>
  </Container>
};
export { ApplicatorPage };
